import { detect } from "detect-browser";
import { action, makeAutoObservable, ObservableMap } from "mobx";
import { Presence } from "revolt-api/src";
import { API, Client, Nullable } from "revolt.js";

import { injectController } from "../../lib/window";

import { getApplicationState } from "../../mobx/State";
import Auth from "../../mobx/stores/Auth";

import { resetMemberSidebarFetched } from "../../components/navigation/right/MemberSidebar";
import { modalController } from "../modals/ModalController";
import { takeError } from "./jsx/error";
import Session from "./Session";

/**
 * Controls the lifecycles of clients
 */
class ClientController {
    /**
     * API client
     */
    private apiClient: Client;

    /**
     * Server configuration
     */
    private configuration: API.RevoltConfig | null;

    /**
     * Map of user IDs to sessions
     */
    private sessions: ObservableMap<string, Session>;

    /**
     * User ID of active session
     */
    private current: Nullable<string>;

    constructor() {
        this.apiClient = new Client({
            apiURL: import.meta.env.VITE_API_URL,
        });

        // ! FIXME: loop until success infinitely
        this.apiClient
            .fetchConfiguration()
            .then(() => (this.configuration = this.apiClient.configuration!));

        this.configuration = null;
        this.sessions = new ObservableMap();
        this.current = null;
        this.login = this.login.bind(this);
        this.logoutCurrent = this.logoutCurrent.bind(this);

        // Inject globally
        injectController("client", this);
        makeAutoObservable(this, {
            pickNextSession: action,
            hydrate: action,
            addSession: action,
            logout: action,
            logoutCurrent: action,
            switchAccount: action,
        });

        let swappedPresence: Presence | null = null;
        window.platform.on.afk((isAfk) => {
            console.log("fired isAfk", isAfk);
            const me = this.activeSession?.client?.user;
            if (!me) {
                return;
            }

            let presence: Presence;
            if (isAfk) {
                swappedPresence = me.status?.presence || null;
                presence = "Idle";
            } else {
                presence = swappedPresence || "Focus";
            }

            this.activeSession?.client?.users.setPresence(presence);
            console.log("presence changed to", presence);
        });
    }

    /**
     * Get the currently selected session
     * @returns Active Session
     */
    get activeSession() {
        return this.sessions.get(this.current!);
    }

    /**
     * Get the currently ready client
     * @returns Ready Client
     */
    get readyClient() {
        const session = this.activeSession;
        return session && session.ready ? session.client! : undefined;
    }

    /**
     * Get an unauthenticated instance of the Revolt.js Client
     * @returns API Client
     */
    get anonymousClient() {
        return this.apiClient;
    }

    /**
     * Get the next available client (either from session or API)
     * @returns Revolt.js Client
     */
    get availableClient() {
        return this.activeSession?.client ?? this.apiClient;
    }

    /**
     * Fetch server configuration
     * @returns Server Configuration
     */
    get serverConfig() {
        return this.configuration;
    }

    /**
     * Check whether we are logged in right now
     * @returns Whether we are logged in
     */
    get isLoggedIn() {
        return this.current !== null;
    }

    /**
     * Check whether we are currently ready
     * @returns Whether we are ready to render
     */
    get isReady() {
        return this.activeSession?.ready;
    }

    pickNextSession() {
        this.switchAccount(
            this.current ?? this.sessions.keys().next().value ?? null,
        );
    }

    /**
     * Hydrate sessions and start client lifecycles.
     * @param auth Authentication store
     */
    hydrate(auth: Auth) {
        for (const entry of auth.accounts) {
            this.addSession(entry, "existing");
        }

        this.pickNextSession();
    }

    /**
     * Start a new client lifecycle
     * @param entry Session Information
     * @param knowledge Whether the session is new or existing
     */
    addSession(
        entry: { session: SessionPrivate; apiUrl?: string },
        knowledge: "new" | "existing",
    ) {
        const user_id = entry.session.user_id!;

        const session = new Session();
        this.sessions.set(user_id, session);
        this.pickNextSession();

        session
            .emit({
                action: "LOGIN",
                session: entry.session,
                apiUrl: entry.apiUrl,
                configuration: this.configuration!,
                knowledge,
            })
            .catch((err) => {
                const error = takeError(err);
                if (error === "Unauthorized") {
                    this.sessions.delete(user_id);
                    this.current = null;
                    this.pickNextSession();
                    getApplicationState().auth.removeSession(user_id);
                    modalController.push({ type: "signed_out" });
                    session.destroy();
                } else {
                    modalController.push({
                        type: "error",
                        error,
                    });
                }
            })
            .finally(() => {
                session.client?.on("called/user", (user) => {
                    modalController.push({
                        type: "called_by_user",
                        user,
                    });
                });
            });
    }

    /**
     * Login given a set of credentials
     * @param credentials Credentials
     */
    async login(credentials: API.DataLogin) {
        const browser = detect();

        // Generate a friendly name for this browser
        let friendly_name;
        if (browser) {
            let { name } = browser;
            const { os } = browser;
            let isiPad;
            if (window.isNative) {
                friendly_name = `Revolt Desktop on ${os}`;
            } else {
                if (name === "ios") {
                    name = "safari";
                } else if (name === "fxios") {
                    name = "firefox";
                } else if (name === "crios") {
                    name = "chrome";
                }
                if (os === "Mac OS" && navigator.maxTouchPoints > 0)
                    isiPad = true;
                friendly_name = `${name} on ${isiPad ? "iPadOS" : os}`;
            }
        } else {
            friendly_name = "Unknown Device";
        }

        /// TODO: Generate and use openapi structs (for now any type)
        // Try to login with given credentials
        let session = await this.apiClient.api.post("/ldap/login", {
            ...credentials,
            friendly_name,
        } as any);

        // Prompt for MFA verificaiton if necessary
        if (session.result === "MFA") {
            const { allowed_methods } = session;
            while (session.result === "MFA") {
                const mfa_response: API.MFAResponse | undefined =
                    await new Promise((callback) =>
                        modalController.push({
                            type: "mfa_flow",
                            state: "unknown",
                            available_methods: allowed_methods,
                            callback,
                        }),
                    );

                if (typeof mfa_response === "undefined") {
                    break;
                }

                try {
                    session = await this.apiClient.api.post(
                        "/auth/session/login",
                        {
                            mfa_response,
                            mfa_ticket: session.ticket,
                            friendly_name,
                        },
                    );
                } catch (err) {
                    console.error("Failed login:", err);
                }
            }

            if (session.result === "MFA") {
                throw "Cancelled";
            }
        }

        // Start client lifecycle
        this.addSession(
            {
                session: session as never,
            },
            "new",
        );
    }

    /**
     * Log out of a specific user session
     * @param user_id Target User ID
     */
    logout(user_id: string) {
        const session = this.sessions.get(user_id);
        if (session) {
            if (user_id === this.current) {
                this.current = null;
            }

            this.sessions.delete(user_id);
            this.pickNextSession();
            session.destroy();
        }
    }

    /**
     * Logout of the current session
     */
    logoutCurrent() {
        if (this.current) {
            this.logout(this.current);
        }
    }

    /**
     * Switch to another user session
     * @param user_id Target User ID
     */
    switchAccount(user_id: string | null) {
        this.current = user_id;

        // This will allow account switching to work more seamlessly,
        // maybe it'll be properly / fully implemented at some point.
        resetMemberSidebarFetched();
    }
}

const clientController = new ClientController();

export function getClientController() {
    return clientController;
}

/**
 * Get the currently active session.
 * @returns Session
 */
export function useSession() {
    return clientController.activeSession;
}

/**
 * Get the currently active client or an unauthorised
 * client for API requests, whichever is available.
 * @returns Revolt.js Client
 */
export function useClient() {
    return clientController.availableClient;
}

/**
 * Get unauthorised client for API requests.
 * @returns Revolt.js Client
 */
export function useApi() {
    return clientController.anonymousClient.api;
}
